import React, { JSX, useState } from 'react'
import { Link } from 'react-router'
import classNames from 'classnames'
import { PlusIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { eventPath, NEW_EVENT_PATH } from 'lib/paths'
import { useEntitlements } from 'context/plan'

export interface EventListItem {
  eventId: string
  name: string
  date?: string
  status: 'INACTIVE' | 'ACTIVE' | 'ENDED'
}

const STATUS_DOT: Record<EventListItem['status'], string> = {
  ACTIVE: 'bg-emerald-500',
  INACTIVE: 'bg-amber-500',
  ENDED: 'bg-zinc-500'
}

const EventRow = ({
  event,
  selectedEventId,
  showsLifecycleChrome
}: {
  event: EventListItem
  selectedEventId?: string
  showsLifecycleChrome: boolean
}): JSX.Element => {
  const isSelected = event.eventId === selectedEventId
  return (
    <li>
      <Link
        to={eventPath(event.eventId)}
        data-testid={`event-sidebar-${event.eventId}`}
        className={classNames(
          'group flex flex-col gap-1 rounded-md px-3 py-2.5 text-sm transition-colors',
          isSelected
            ? 'bg-brand/10 text-dm-text-primary ring-1 ring-brand/30'
            : 'text-dm-text-secondary hover:bg-white/5 hover:text-dm-text-primary'
        )}
      >
        <div className='flex items-start gap-2'>
          {showsLifecycleChrome && (
            <span className={classNames('mt-1.5 h-2 w-2 shrink-0 rounded-full', STATUS_DOT[event.status])} />
          )}
          <span className='min-w-0 flex-1 break-words font-medium'>{event.name}</span>
        </div>
        {event.date !== undefined && (
          <span className={classNames('text-xs text-dm-text-accent', showsLifecycleChrome ? 'pl-4' : 'pl-0')}>{event.date}</span>
        )}
      </Link>
    </li>
  )
}

export const EventsSidebar = ({
  events,
  selectedEventId
}: {
  events: EventListItem[]
  selectedEventId?: string
}): JSX.Element => {
  const { enforcedLimitFor, limitFor, usage, status, featureFor } = useEntitlements()
  const eventCap = enforcedLimitFor('eventsPerAccount')
  const atEventCap = eventCap !== null && usage.events >= eventCap
  const streamsLocked = status === 'ready' && limitFor('eventsPerAccount') === 0
  const showsLifecycleChrome = status !== 'ready' || featureFor('interactiveContent')
  const activeCount = events.filter((e) => e.status === 'ACTIVE').length
  const liveEvents = events.filter((e) => e.status !== 'ENDED')
  const endedEvents = events.filter((e) => e.status === 'ENDED')
  const [showEnded, setShowEnded] = useState(false)

  return (
    <aside
      data-testid='events-sidebar'
      className='flex h-screen w-[14.58rem] flex-col border-r border-white/5 bg-dm-bg-secondary sticky top-0'
    >
      <div className='flex items-center justify-between border-b border-white/5 px-5 pt-5 pb-4'>
        <h2 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
          Streams
        </h2>
        {!streamsLocked && (
          atEventCap
            ? (
              <button
                type='button'
                disabled
                title='Stream limit reached on your plan, upgrade to create more'
                data-testid='events-sidebar-create'
                className='flex h-7 w-7 cursor-not-allowed items-center justify-center rounded-md text-dm-text-accent opacity-40'
              >
                <PlusIcon className='size-4' aria-hidden='true' />
                <span className='sr-only'>Stream limit reached</span>
              </button>
              )
            : (
              <Link
                to={NEW_EVENT_PATH}
                title='Create stream'
                data-testid='events-sidebar-create'
                className='flex h-7 w-7 items-center justify-center rounded-md text-dm-text-accent hover:bg-white/5 hover:text-brand transition-colors'
              >
                <PlusIcon className='size-4' aria-hidden='true' />
                <span className='sr-only'>Create stream</span>
              </Link>
              )
        )}
      </div>

      <nav className='flex-1 overflow-y-auto px-2 py-3'>
        <ul className='space-y-1'>
          {liveEvents.map((event) => (
            <EventRow key={event.eventId} event={event} selectedEventId={selectedEventId} showsLifecycleChrome={showsLifecycleChrome} />
          ))}
        </ul>

        {endedEvents.length > 0 && (
          <div className='mt-3 border-t border-white/5 pt-3'>
            <button
              type='button'
              onClick={() => setShowEnded((v) => !v)}
              data-testid='events-sidebar-archived-toggle'
              aria-expanded={showEnded}
              className='flex w-full items-center justify-between rounded-md px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent hover:text-dm-text-secondary'
            >
              <span>Archived ({endedEvents.length})</span>
              <ChevronDownIcon
                className={classNames('size-3.5 transition-transform', showEnded && 'rotate-180')}
                aria-hidden='true'
              />
            </button>
            {showEnded && (
              <ul className='mt-1 space-y-1'>
                {endedEvents.map((event) => (
                  <EventRow key={event.eventId} event={event} selectedEventId={selectedEventId} showsLifecycleChrome={showsLifecycleChrome} />
                ))}
              </ul>
            )}
          </div>
        )}
      </nav>

      <div className='border-t border-white/5 px-5 py-4'>
        <div className='flex items-center justify-between text-xs'>
          <span className='font-semibold uppercase tracking-wider text-dm-text-accent'>
            Live Streams
          </span>
          <span
            data-testid='active-events-count'
            className='inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-emerald-500/15 px-2 text-xs font-semibold text-emerald-400'
          >
            {activeCount}
          </span>
        </div>
      </div>
    </aside>
  )
}
